import { useEffect, useRef, RefObject, useState } from "react";
import { PDFDocumentProxy, RenderTask } from "pdfjs-dist";

import { PreviewState } from "./useBlobPreview";

interface UsePdfPageRendererOptions {
  canvasRef: RefObject<HTMLCanvasElement>;
  canvasWrapperRef: RefObject<HTMLDivElement>;
  pageNumber: number;
  pdfDocument: PDFDocumentProxy | null;
  previewState: PreviewState | null;
  onError: () => void;
}

interface UsePdfPageRendererResult {
  isRenderingPdf: boolean;
  hasRenderedPdf: boolean;
}

const MIN_OUTPUT_SCALE = 2;
const MAX_OUTPUT_SCALE = 3;
const OUTPUT_SCALE_MULTIPLIER = 1.5;

export const usePdfPageRenderer = ({
  canvasRef,
  canvasWrapperRef,
  pageNumber,
  pdfDocument,
  previewState,
  onError,
}: UsePdfPageRendererOptions): UsePdfPageRendererResult => {
  const [isRenderingPdf, setIsRenderingPdf] = useState(false);
  const [hasRenderedPdf, setHasRenderedPdf] = useState(false);

  const onErrorRef = useRef(onError);
  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  useEffect(() => {
    if (!previewState || previewState.kind !== "pdf" || !pdfDocument) {
      setHasRenderedPdf(false);
    }
  }, [pdfDocument, previewState]);

  useEffect(() => {
    if (!previewState || previewState.kind !== "pdf" || !pdfDocument) {
      return;
    }

    const canvas = canvasRef.current;
    const canvasWrapper = canvasWrapperRef.current;

    if (!canvas || !canvasWrapper) {
      return;
    }

    let renderTask: RenderTask | null = null;
    let isCancelled = false;
    let resizeObserver: ResizeObserver | null = null;

    const getAvailableSize = () => {
      const wrapperStyles = window.getComputedStyle(canvasWrapper);
      const horizontalPadding =
        parseFloat(wrapperStyles.paddingLeft) +
        parseFloat(wrapperStyles.paddingRight);
      const verticalPadding =
        parseFloat(wrapperStyles.paddingTop) +
        parseFloat(wrapperStyles.paddingBottom);
      return {
        width: canvasWrapper.clientWidth - horizontalPadding,
        height: canvasWrapper.clientHeight - verticalPadding,
      };
    };

    const renderPage = async () => {
      if (isCancelled) {
        return;
      }

      const { width: availableWidth, height: availableHeight } =
        getAvailableSize();

      if (availableWidth <= 0 || availableHeight <= 0) {
        // Wait for the wrapper to get a positive size before rendering.
        resizeObserver = new ResizeObserver(() => {
          const { width, height } = getAvailableSize();
          if (width > 0 && height > 0) {
            resizeObserver?.disconnect();
            resizeObserver = null;
            void renderPage();
          }
        });
        resizeObserver.observe(canvasWrapper);
        return;
      }

      setIsRenderingPdf(true);

      try {
        const page = await pdfDocument.getPage(pageNumber);

        if (isCancelled) {
          return;
        }

        const unscaledViewport = page.getViewport({ scale: 1 });
        const scale = Math.max(
          Math.min(availableWidth / unscaledViewport.width, 1.75),
          0.1,
        );
        const viewport = page.getViewport({ scale });
        const context = canvas.getContext("2d");

        if (!context) {
          return;
        }

        const outputScale = Math.min(
          Math.max(
            (window.devicePixelRatio || 1) * OUTPUT_SCALE_MULTIPLIER,
            MIN_OUTPUT_SCALE,
          ),
          MAX_OUTPUT_SCALE,
        );
        const nextCanvasWidth = Math.floor(viewport.width * outputScale);
        const nextCanvasHeight = Math.floor(viewport.height * outputScale);
        const renderCanvas = document.createElement("canvas");
        renderCanvas.width = nextCanvasWidth;
        renderCanvas.height = nextCanvasHeight;
        const renderContext = renderCanvas.getContext("2d");

        if (!renderContext) {
          return;
        }

        renderTask = page.render({
          canvasContext: renderContext,
          viewport,
          transform:
            outputScale === 1
              ? undefined
              : [outputScale, 0, 0, outputScale, 0, 0],
        });
        await renderTask.promise;

        if (isCancelled) {
          return;
        }

        canvas.width = nextCanvasWidth;
        canvas.height = nextCanvasHeight;
        context.setTransform(1, 0, 0, 1, 0, 0);
        context.clearRect(0, 0, canvas.width, canvas.height);
        context.drawImage(renderCanvas, 0, 0);
        setHasRenderedPdf(true);

        // If the wrapper changed size while we were rendering (e.g. dialog
        // open animation still settling), re-render at the stable size.
        const { width: settledWidth } = getAvailableSize();
        if (!isCancelled && Math.abs(settledWidth - availableWidth) > 1) {
          void renderPage();
        }
      } catch {
        if (!isCancelled) {
          onErrorRef.current();
        }
      } finally {
        if (!isCancelled) {
          setIsRenderingPdf(false);
        }
      }
    };

    renderPage();
    return () => {
      isCancelled = true;
      resizeObserver?.disconnect();
      if (renderTask) {
        renderTask.cancel();
      }
    };
  }, [canvasRef, canvasWrapperRef, pageNumber, pdfDocument, previewState]);

  return { hasRenderedPdf, isRenderingPdf };
};
